import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { FiFilter, FiGrid, FiList } from 'react-icons/fi';
import ProductCard from '../components/ProductCard';
import { Product } from '../types';
import { productApi } from '../services/api';

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
`;

const Header = styled.div`
  margin-bottom: 2rem;
`;

const Title = styled.h1`
  font-size: 2rem;
  font-weight: bold;
  color: #1a1a1a;
  margin-bottom: 0.5rem;
`;

const Subtitle = styled.p`
  color: #666;
  font-size: 1.1rem;
`;

const FilterBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  padding: 1rem;
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  gap: 1rem;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const FilterLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
`;

const FilterButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border: 1px solid #e0e0e0;
  background: white;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    border-color: #1a1a1a;
  }
`;

const FilterSelect = styled.select`
  padding: 0.5rem 1rem;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  background: white;
  cursor: pointer;
  outline: none;

  &:focus {
    border-color: #1a1a1a;
  }
`;

const ViewToggle = styled.div`
  display: flex;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  overflow: hidden;
`;

const ViewButton = styled.button<{ active: boolean }>`
  padding: 0.5rem 1rem;
  border: none;
  background: ${props => props.active ? '#1a1a1a' : 'white'};
  color: ${props => props.active ? 'white' : '#666'};
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: ${props => props.active ? '#1a1a1a' : '#f5f5f5'};
  }
`;

const ResultsInfo = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  color: #666;
`;

const ProductGrid = styled.div<{ viewMode: 'grid' | 'list' }>`
  display: grid;
  grid-template-columns: ${props => 
    props.viewMode === 'grid' 
      ? 'repeat(auto-fill, minmax(280px, 1fr))' 
      : '1fr'
  };
  gap: 2rem;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
  font-size: 1.1rem;
  color: #666;
`;

const ErrorContainer = styled.div`
  text-align: center;
  padding: 2rem;
  color: #e74c3c;
`;

const NoResults = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  color: #666;
`;

const LoadMoreButton = styled.button`
  display: block;
  margin: 3rem auto 0;
  padding: 1rem 2rem;
  background: #1a1a1a;
  color: white;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: #333;
    transform: translateY(-1px);
  }

  &:disabled {
    background: #ccc;
    cursor: not-allowed;
    transform: none;
  }
`;

const ProductsPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [sortBy, setSortBy] = useState('newest');
  const [totalProducts, setTotalProducts] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const currentOffsetRef = useRef(0);

  const category = searchParams.get('category');
  const searchQuery = searchParams.get('q');
  const brandId = searchParams.get('brandId');
  const limit = 12;

  const fetchProducts = useCallback(async (reset = false) => {
    try {
      setLoading(true);
      setError(null);

      // Reset offset if needed
      if (reset) {
        currentOffsetRef.current = 0;
      }

      // Use ref value for offset
      const offset = currentOffsetRef.current;
      
      let result: {
        products: Product[];
        total: number;
        pagination: {
          limit: number;
          offset: number;
          hasMore: boolean;
        };
      };
      
      if (searchQuery) {
        // Search by query
        const searchResults = await productApi.searchProductsByQuery(searchQuery, undefined, limit);
        result = {
          products: searchResults,
          total: searchResults.length,
          pagination: {
            limit,
            offset,
            hasMore: false
          }
        };
      } else {
        // Regular product search with filters
        result = await productApi.searchProducts({
          brandId: brandId || undefined,
          category: category || undefined,
          limit,
          offset,
          isActive: true
        });
      }

      if (reset) {
        setProducts(result.products);
      } else {
        setProducts(prev => [...prev, ...result.products]);
      }

      // Update offset ref with actual fetched count
      currentOffsetRef.current += result.products.length;

      setTotalProducts(result.total);
      setHasMore(result.pagination.hasMore);

    } catch (err) {
      setError('Ürünler yüklenirken bir hata oluştu');
      console.error('Error fetching products:', err);
    } finally {
      setLoading(false);
    }
  }, [category, searchQuery, brandId, limit]);

  useEffect(() => {
    fetchProducts(true);
  }, [category, searchQuery, brandId, sortBy, fetchProducts]);

  const handleLoadMore = () => {
    if (!loading && hasMore) {
      fetchProducts(false);
    }
  };

  const handleSortChange = (newSortBy: string) => {
    setSortBy(newSortBy);
    currentOffsetRef.current = 0;
  };

  const getPageTitle = () => {
    if (searchQuery) return `"${searchQuery}" için arama sonuçları`;
    if (category) return category.charAt(0).toUpperCase() + category.slice(1);
    if (brandId) return 'Marka Ürünleri';
    return 'Tüm Ürünler';
  };

  const getPageSubtitle = () => {
    if (searchQuery) return `${totalProducts} ürün bulundu`;
    if (category) return `${category} kategorisindeki ürünler`;
    if (brandId) return 'Seçili markanın ürünleri';
    return 'Tüm ürün koleksiyonumuzu keşfedin';
  };

  return (
    <Container>
      <Header>
        <Title>{getPageTitle()}</Title>
        <Subtitle>{getPageSubtitle()}</Subtitle>
      </Header>

      <FilterBar>
        <FilterLeft>
          <FilterButton>
            <FiFilter size={18} />
            Filtrele
          </FilterButton>
          
          <FilterSelect 
            value={sortBy} 
            onChange={(e) => handleSortChange(e.target.value)}
          >
            <option value="newest">En Yeni</option>
            <option value="oldest">En Eski</option>
            <option value="price-low">Fiyat (Düşük-Yüksek)</option>
            <option value="price-high">Fiyat (Yüksek-Düşük)</option>
            <option value="name">İsim (A-Z)</option>
          </FilterSelect>
        </FilterLeft>

        <ViewToggle>
          <ViewButton 
            active={viewMode === 'grid'} 
            onClick={() => setViewMode('grid')}
          >
            <FiGrid size={18} />
          </ViewButton>
          <ViewButton 
            active={viewMode === 'list'} 
            onClick={() => setViewMode('list')}
          >
            <FiList size={18} />
          </ViewButton>
        </ViewToggle>
      </FilterBar>

      {!loading && !error && (
        <ResultsInfo>
          <span>{totalProducts} ürün gösteriliyor</span>
          <span>Sayfa başına {limit} ürün</span>
        </ResultsInfo>
      )}

      {loading && products.length === 0 && (
        <LoadingContainer>
          Ürünler yükleniyor...
        </LoadingContainer>
      )}

      {error && (
        <ErrorContainer>
          {error}
        </ErrorContainer>
      )}

      {!loading && !error && products.length === 0 && (
        <NoResults>
          <h3>Ürün bulunamadı</h3>
          <p>Arama kriterlerinizi değiştirerek tekrar deneyin.</p>
        </NoResults>
      )}

      {!error && products.length > 0 && (
        <>
          <ProductGrid viewMode={viewMode}>
            {products.map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </ProductGrid>

          {hasMore && (
            <LoadMoreButton 
              onClick={handleLoadMore}
              disabled={loading}
            >
              {loading ? 'Yükleniyor...' : 'Daha Fazla Ürün Yükle'}
            </LoadMoreButton>
          )}
        </>
      )}
    </Container>
  );
};

export default ProductsPage;
